import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import {
    getIsAnyCreatingNewState,
    getIsShowingWithoutId
} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';

const MADBOT_DEFAULT_PROJECT_TITLE = 'MadBot AI Studio Project';

const messages = defineMessages({
    defaultProjectTitle: {
        id: 'gui.gui.defaultProjectTitle',
        description: 'Default title for project',
        defaultMessage: MADBOT_DEFAULT_PROJECT_TITLE
    }
});

/* Higher Order Component to get and set the project title
 * @param {React.Component} WrappedComponent component to receive project title related props
 * @returns {React.Component} component with project loading behavior
 */
const TitledHOC = function (WrappedComponent) {
    class TitledComponent extends React.Component {
        componentDidMount () {
            this.handleReceivedProjectTitle(this.props.projectTitle);
        }

        componentDidUpdate (prevProps) {
            if (this.props.projectTitle !== prevProps.projectTitle) {
                this.handleReceivedProjectTitle(this.props.projectTitle);
            }

            // If project is a new default project and has loaded,
            // reset title to MadBot default title.
            if (this.props.isShowingWithoutId && prevProps.isAnyCreatingNewState) {
                this.props.onUpdateProjectTitle(MADBOT_DEFAULT_PROJECT_TITLE);
                this.props.onChangedProjectTitle(MADBOT_DEFAULT_PROJECT_TITLE);
            }

            // If the reduxProjectTitle changed, report that change.
            if (
                this.props.reduxProjectTitle !== prevProps.reduxProjectTitle &&
                this.props.reduxProjectTitle !== this.props.projectTitle
            ) {
                this.props.onUpdateProjectTitle(this.props.reduxProjectTitle);
            }
        }

        handleReceivedProjectTitle (requestedTitle) {
            let newTitle = requestedTitle;

            // Force MadBot default title whenever Scratch default title appears.
            if (
                newTitle === null ||
                typeof newTitle === 'undefined' ||
                newTitle === '' ||
                newTitle.trim() === 'Scratch Project'
            ) {
                newTitle = MADBOT_DEFAULT_PROJECT_TITLE;
            }

            this.props.onChangedProjectTitle(newTitle);
            return newTitle;
        }

        render () {
            const {
                /* eslint-disable no-unused-vars */
                intl,
                isAnyCreatingNewState,
                isShowingWithoutId,
                onChangedProjectTitle,
                onUpdateProjectTitle,
                projectTitle,
                reduxProjectTitle,
                /* eslint-enable no-unused-vars */
                ...componentProps
            } = this.props;

            return (
                <WrappedComponent
                    {...componentProps}
                />
            );
        }
    }

    TitledComponent.propTypes = {
        intl: intlShape,
        isAnyCreatingNewState: PropTypes.bool,
        isShowingWithoutId: PropTypes.bool,
        onChangedProjectTitle: PropTypes.func,
        onUpdateProjectTitle: PropTypes.func,
        projectTitle: PropTypes.string,
        reduxProjectTitle: PropTypes.string
    };

    TitledComponent.defaultProps = {
        onUpdateProjectTitle: () => {}
    };

    const mapStateToProps = state => {
        const loadingState = state.scratchGui.projectState.loadingState;

        return {
            isAnyCreatingNewState: getIsAnyCreatingNewState(loadingState),
            isShowingWithoutId: getIsShowingWithoutId(loadingState),
            reduxProjectTitle: state.scratchGui.projectTitle
        };
    };

    const mapDispatchToProps = dispatch => ({
        onChangedProjectTitle: title => dispatch(setProjectTitle(title))
    });

    return injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps
    )(TitledComponent));
};

export {
    TitledHOC as default
};